import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { mediaAPI } from '../services/api';

/**
 * useS3Upload — handles the full direct-to-S3 (or local-fallback) upload lifecycle.
 *
 * Lifecycle per file:
 *   1. Request a presigned POST from our API (asserts folder + size + MIME allowed
 *      and reserves the MediaAsset row in pending_upload state).
 *   2. POST the file straight to S3 (or `/api/media/_local-upload/:token` in dev).
 *   3. Call confirm-upload on our API so the server HEADs the object, writes the
 *      public URL, and flips the row to ready.
 *
 * Features:
 *   - Pre-upload client-side validation (size + mime) to short-circuit bad files.
 *   - Per-file progress tracking (0–100) via XHR upload events.
 *   - Cancellation (cleans up pending row if we bail before step 3).
 *   - Automatic retry with exponential backoff on transient network failure.
 *   - Concurrency control (default: 2 simultaneous uploads).
 *   - Unmount safety (ref tracked so setState never fires on dead component).
 */

const DEFAULT_CONCURRENCY = 2;
const MAX_RETRIES = 2;

const INITIAL_FILE_STATE = (file) => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  file,
  name: file.name,
  size: file.size,
  progress: 0,
  status: 'queued', // 'queued' | 'uploading' | 'confirming' | 'done' | 'error' | 'cancelled'
  error: null,
  asset: null,
  retries: 0,
});

// Very small concurrency limiter to avoid clobbering slower networks.
function createPool(limit) {
  const state = { active: 0, queue: [] };
  const runNext = () => {
    if (state.active >= limit || !state.queue.length) return;
    const { task, resolve, reject } = state.queue.shift();
    state.active += 1;
    Promise.resolve()
      .then(task)
      .then((v) => { state.active -= 1; resolve(v); runNext(); })
      .catch((e) => { state.active -= 1; reject(e); runNext(); });
    runNext(); // fill remaining slots
  };
  return (task) => new Promise((resolve, reject) => {
    state.queue.push({ task, resolve, reject });
    runNext();
  });
}

export default function useS3Upload({ folder, concurrency = DEFAULT_CONCURRENCY, onAssetReady } = {}) {
  const [queue, setQueue] = useState([]);      // [FileUploadState]
  const [isUploading, setIsUploading] = useState(false);
  const mountedRef = useRef(true);
  const xhrsRef = useRef(new Map()); // id → XMLHttpRequest (for cancel)

  useEffect(() => {
    mountedRef.current = true;
    // Capture ref value inside the effect so the cleanup function refers to
    // the exact Map that was live when the component mounted.
    const pending = xhrsRef.current;
    return () => {
      mountedRef.current = false;
      for (const xhr of pending.values()) {
        try { xhr.abort(); } catch (_) { /* ignore */ }
      }
      pending.clear();
    };
  }, []);

  const updateItem = useCallback((id, patch) => {
    if (!mountedRef.current) return;
    setQueue((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  }, []);

  const removeItem = useCallback((id) => {
    if (!mountedRef.current) return;
    setQueue((prev) => prev.filter((it) => it.id !== id));
  }, []);

  const cancel = useCallback((id) => {
    const xhr = xhrsRef.current.get(id);
    if (xhr) { try { xhr.abort(); } catch (_) { /* ignore */ } }
    xhrsRef.current.delete(id);
    updateItem(id, { status: 'cancelled', error: null });
  }, [updateItem]);

  const clearFinished = useCallback(() => {
    setQueue((prev) => prev.filter((it) => !['done', 'cancelled', 'error'].includes(it.status)));
  }, []);

  const putFileToS3 = useCallback((item, uploadInstructions) => {
    return new Promise((resolve, reject) => {
      const { url, fields, isLocal } = uploadInstructions;
      const form = new FormData();
      // S3 presigned POST requires fields FIRST, then the file, in that exact order.
      if (fields && typeof fields === 'object') {
        for (const [k, v] of Object.entries(fields)) form.append(k, v);
      }
      form.append('file', item.file, item.name);

      const xhr = new XMLHttpRequest();
      xhrsRef.current.set(item.id, xhr);
      xhr.open('POST', url, true);
      // S3 does NOT want auth headers on a presigned POST (signature covers it)
      // and our local endpoint consumes the one-time token in the URL.
      xhr.upload.onprogress = (evt) => {
        if (!evt.lengthComputable) return;
        const progress = Math.round((evt.loaded / evt.total) * 100);
        updateItem(item.id, { progress });
      };
      xhr.onload = () => {
        xhrsRef.current.delete(item.id);
        if (xhr.status >= 200 && xhr.status < 300) return resolve();
        const err = new Error(`Upload failed (HTTP ${xhr.status}) ${isLocal ? xhr.responseText : ''}`.trim());
        err.transient = xhr.status >= 500 || xhr.status === 0;
        reject(err);
      };
      xhr.onerror = () => {
        xhrsRef.current.delete(item.id);
        const err = new Error('Network error during upload');
        err.transient = true;
        reject(err);
      };
      xhr.onabort = () => {
        xhrsRef.current.delete(item.id);
        const err = new Error('cancelled');
        err.cancelled = true;
        reject(err);
      };
      xhr.send(form);
    });
  }, [updateItem]);

  const uploadOne = useCallback(async (item) => {
    try {
      updateItem(item.id, { status: 'uploading', progress: 0, error: null });

      // 1) Request signed URL
      const signedResp = await mediaAPI.getSignedUrl({
        folder: folder || item.folder,
        mimeType: item.file.type || 'application/octet-stream',
        sizeBytes: item.file.size,
        originalName: item.name,
      });
      const body = signedResp.data?.data || signedResp.data;
      const asset = body.asset;
      const uploadInstructions = body.upload;

      // 2) Upload to S3 / local — with one automatic retry on transient failure
      let attempt = 0;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        try {
          await putFileToS3(item, uploadInstructions);
          break;
        } catch (err) {
          if (err.cancelled) throw err;
          if (err.transient && attempt < MAX_RETRIES) {
            attempt += 1;
            const backoff = 500 * 2 ** attempt;
            await new Promise((r) => setTimeout(r, backoff));
            continue;
          }
          throw err;
        }
      }

      // 3) Confirm with backend
      updateItem(item.id, { status: 'confirming', progress: 100 });
      const confirmResp = await mediaAPI.confirmUpload({ assetId: asset._id });
      const confirmed = confirmResp.data?.data || confirmResp.data;

      updateItem(item.id, { status: 'done', asset: confirmed });
      if (typeof onAssetReady === 'function') onAssetReady(confirmed);
      return confirmed;
    } catch (err) {
      if (err.cancelled) {
        updateItem(item.id, { status: 'cancelled' });
        return null;
      }
      const message = err?.response?.data?.message || err?.message || 'Upload failed';
      updateItem(item.id, { status: 'error', error: message });
      return null;
    }
  }, [folder, onAssetReady, putFileToS3, updateItem]);

  const uploadFiles = useCallback(async (files) => {
    const items = Array.from(files).map(INITIAL_FILE_STATE);
    if (items.length === 0) return [];

    setQueue((prev) => [...prev, ...items]);
    setIsUploading(true);
    const pool = createPool(concurrency);
    const results = await Promise.all(items.map((it) => pool(() => uploadOne(it))));
    if (mountedRef.current) setIsUploading(false);
    return results.filter(Boolean);
  }, [concurrency, uploadOne]);

  return {
    uploadFiles,
    queue,
    isUploading,
    cancel,
    clearFinished,
    removeItem,
  };
}

// Axios error → friendly message extractor for UI
export function formatUploadError(err) {
  if (!err) return 'خطأ غير معروف';
  const msg = err?.response?.data?.message || err?.message;
  return msg || 'تعذّر رفع الملف، حاول مجدداً';
}

// Keep a simple axios re-export so callers can use it for side requests if needed.
export { axios };
